import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { ArrowLeft, Loader2, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { OTPInput } from '@/components/OTPInput'
import {
  ApiHttpError,
  fetchCompanies,
  sendEmailVerification,
  verifyEmail,
} from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { defaultLocale } from '@/lib/locales'

import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function VerifyEmailPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  useDocumentTitle(t('verifyEmail.pageTitle', 'Vérification email'))
  const email = searchParams.get('email') || ''
  const fromLogin = searchParams.get('from') === 'login'

  const setTokens = useAuthStore((s) => s.setTokens)
  const clearAuth = useAuthStore((s) => s.clear)

  const [code, setCode] = useState('')
  const [hasError, setHasError] = useState(false)
  const [success, setSuccess] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const autoSentRef = useRef(false)

  // Si on vient du login (email non vérifié), envoie un nouveau code automatiquement.
  useEffect(() => {
    if (!fromLogin || !email || autoSentRef.current) return
    autoSentRef.current = true
    void sendEmailVerification(email, locale).catch((err) => {
      console.error('[VerifyEmail] auto resend failed:', err)
    })
  }, [fromLogin, email, locale])

  // Countdown resend
  useEffect(() => {
    if (countdown <= 0) return
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [countdown])

  const verifyMutation = useMutation({
    mutationFn: async (codeStr: string) => verifyEmail(email, codeStr),
    onSuccess: async (data) => {
      if (!data.success || !data.access || !data.refresh) {
        setHasError(true)
        setCode('')
        toast.error(data.error || t('verifyEmail.error.invalid', 'Code invalide'))
        return
      }
      setSuccess(true)
      clearAuth()
      setTokens(data.access, data.refresh)

      // Redirection : si l'utilisateur a déjà une entreprise active → dashboard, sinon onboarding
      try {
        const companies = await fetchCompanies()
        const company = companies.companies?.[0]
        if (company?.has_active_number || company?.phone_number) {
          navigate(`/${locale}/dashboard`, { replace: true })
          return
        }
      } catch (err) {
        // Réponse illisible : on ne sait pas si l'entreprise existe. Le
        // tableau de bord tranche (ou affiche « Connexion difficile »).
        console.error('[VerifyEmail] companies fetch failed:', err)
        navigate(`/${locale}/dashboard`, { replace: true })
        return
      }
      navigate(`/${locale}/onboarding/step1`, { replace: true })
    },
    onError: (error) => {
      setCode('')
      if (error instanceof ApiHttpError) {
        if (error.status === 429) {
          setHasError(true)
          toast.error(
            t('verifyEmail.error.tooManyAttempts') ||
              'Trop de tentatives. Veuillez patienter une minute.',
          )
          return
        }
        const data = error.data as { error?: string }
        setHasError(true)
        toast.error(data?.error || t('verifyEmail.error.invalid', 'Code invalide'))
        return
      }
      console.error('[VerifyEmail]', error)
      setHasError(true)
      toast.error(t('verifyEmail.error.connection', 'Erreur de connexion'))
    },
  })

  const resendMutation = useMutation({
    mutationFn: async () => sendEmailVerification(email, locale),
    onSuccess: () => {
      setCountdown(60)
      toast.success(locale === 'en' ? 'New code sent!' : 'Nouveau code envoyé !')
    },
    onError: () => {
      toast.error(t('verifyEmail.error.resend') || "Impossible d'envoyer un nouveau code")
    },
  })

  const handleChange = (newValue: string) => {
    setHasError(false)
    setCode(newValue)
  }

  const handleComplete = (codeStr: string) => {
    if (verifyMutation.isPending || success) return
    verifyMutation.mutate(codeStr)
  }

  const isLoading = verifyMutation.isPending
  const canResend = countdown <= 0 && !resendMutation.isPending

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white text-slate-900">
      <div className="relative hidden md:flex items-end bg-[#0A192F] text-white p-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(800px 400px at 20% 10%, #ffffff 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-white" /> {t('verifyEmail.badge')}
          </div>
          <h1 className="text-3xl font-semibold leading-tight">{t('verifyEmail.title')}</h1>
          <p className="text-white/80 max-w-md">{t('verifyEmail.subtitle')}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-6 flex items-center gap-2">
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="h-7 w-7"
            />
            <span className="font-semibold">Callizy</span>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-xl mb-4">
              <Mail className="w-6 h-6 text-slate-700" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">{t('verifyEmail.heading')}</h2>
            <p className="text-slate-600 text-sm">
              {t('verifyEmail.codeSentTo')} <strong className="ph-mask">{email || '—'}</strong>
            </p>
          </div>

          <div className="mb-6">
            <OTPInput
              value={code}
              onChange={handleChange}
              onComplete={handleComplete}
              disabled={isLoading || success}
              error={hasError}
              success={success}
            />
          </div>

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600">{t('verifyEmail.success')}</p>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center mb-4">
              <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
            </div>
          )}

          <div className="text-center mb-6">
            <button
              type="button"
              onClick={() => resendMutation.mutate()}
              disabled={!canResend}
              className={
                canResend
                  ? 'text-sm font-medium text-[#0A192F] hover:text-slate-700 transition-colors'
                  : 'text-sm font-medium text-slate-400 cursor-not-allowed'
              }
            >
              {resendMutation.isPending
                ? t('verifyEmail.resend.sending')
                : canResend
                  ? t('verifyEmail.resend.button')
                  : t('verifyEmail.resend.countdown', { seconds: countdown })}
            </button>
          </div>

          <div className="text-center pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => navigate(`/${locale}/login`)}
              className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('verifyEmail.backToLogin')}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
